/**
 * Server template configurations
 * Matches: Server.VBM, Server.Colocation, Server.Dedicated
 *
 * Special features:
 * - Uses capacity_value * commitment_value for quota calculation
 */
import type { TemplateConfig, TemplateField } from '../types'
import { createExpandedRowRender } from '../../expanded-row'
import { capacityQuotaCalculator, defaultIsExpandable } from '../shared'

// ============================================================================
// Fields
// ============================================================================

export const SERVER_VBM_FIELDS: TemplateField[] = [
    {
        key: 'server',
        names: ['server', 'processor'],
        label: 'Server/Processor',
    },
    { key: 'cpu', names: ['cpu', 'vcore', 'vcpu'], label: 'CPU' },
    { key: 'memory', names: ['memory', 'ram'], label: 'Memory' },
    { key: 'disk', names: ['disk', 'ssd', 'hdd', 'storage'], label: 'Disk' },
    { key: 'ip', names: ['ip', 'ip-address', 'ipv4', 'ipv6'], label: 'IP' },
    { key: 'bandwidth', names: ['bandwidth', 'bw'], label: 'Bandwidth' },
    {
        key: 'peak_bandwidth',
        names: ['peak-bandwidth', 'peak_bandwidth', 'peak-bw'],
        label: 'Peak BW',
    },
]

export const SERVER_COLOCATION_FIELDS: TemplateField[] = [
    { key: 'u', names: ['u', 'colocation-u'], label: 'U' },
    { key: 'power', names: ['power', 'nguon'], label: 'Power' },
    { key: 'bandwidth', names: ['bandwidth', 'bw'], label: 'Bandwidth' },
    { key: 'local', names: ['local'], label: 'Local BW' },
    { key: 'global', names: ['global'], label: 'Global BW' },
    { key: 'ip', names: ['ip', 'ip-address', 'ipv4', 'ipv6'], label: 'IP' },
]

// ============================================================================
// Template Configs
// ============================================================================

export const serverVBMTemplate: TemplateConfig = {
    id: 'server-vbm',
    patterns: ['server.vbm', 'baremetal'],
    priority: 10,

    fields: SERVER_VBM_FIELDS,

    expandedRow: {
        createRenderer: createExpandedRowRender,
        isExpandable: defaultIsExpandable,
    },

    calculateQuota: capacityQuotaCalculator, // Special: capacity * commitment
}

export const serverColocationTemplate: TemplateConfig = {
    id: 'server-colocation',
    patterns: ['server.colocation', 'colocation'],
    priority: 10,

    fields: SERVER_COLOCATION_FIELDS,

    expandedRow: {
        createRenderer: createExpandedRowRender,
        isExpandable: defaultIsExpandable,
    },

    calculateQuota: capacityQuotaCalculator, // Special: capacity * commitment
}

export const serverDedicatedTemplate: TemplateConfig = {
    id: 'server-dedicated',
    patterns: ['server.dedicated', 'dedicated'],
    priority: 10,

    fields: SERVER_VBM_FIELDS, // Same as VBM

    expandedRow: {
        createRenderer: createExpandedRowRender,
        isExpandable: defaultIsExpandable,
    },

    calculateQuota: capacityQuotaCalculator,
}

// Export all server templates
export const serverTemplates = [
    serverVBMTemplate,
    serverColocationTemplate,
    serverDedicatedTemplate,
]
